import { create } from "zustand";
import type { Feature, RolePermissions } from "@/shared/rbac";

interface MemberState {
  currentRole: string | string[] | null;
  isAdmin: boolean;
  permissions: RolePermissions | null;
  canRead: (feature: Feature) => boolean;
  canWrite: (feature: Feature) => boolean;
  loading: boolean;
  fetched: boolean;
  error: string | null;
  fetchRole: (force?: boolean) => Promise<void>;
  reset: () => void;
}

// Last known access per org, used only to draw the sidebar before live permissions load.
// Page access checks always wait for the live permissions below.
type CachedNavAccess = { isAdmin: boolean; permissions: RolePermissions | null };
const navAccessKey = (orgId: string) => `querygen:nav-access:${orgId}`;

export function readCachedNavAccess(orgId: string): CachedNavAccess | null {
  try {
    const raw = window.localStorage.getItem(navAccessKey(orgId));
    return raw ? JSON.parse(raw) as CachedNavAccess : null;
  } catch {
    return null;
  }
}

function writeCachedNavAccess(orgId: string, access: CachedNavAccess | null) {
  try {
    if (access) window.localStorage.setItem(navAccessKey(orgId), JSON.stringify(access));
    else window.localStorage.removeItem(navAccessKey(orgId));
  } catch {
    // Storage may be unavailable (private mode); the sidebar then waits for live permissions.
  }
}

// Module-level promise to prevent race conditions
let fetchRolePromise: Promise<void> | null = null;
let fetchRoleGeneration = 0;

export const useMemberStore = create<MemberState>((set, get) => ({
  currentRole: null,
  isAdmin: false,
  permissions: null,
  canRead: (feature) => get().isAdmin || get().permissions?.[feature]?.read === true,
  canWrite: (feature) => get().isAdmin || get().permissions?.[feature]?.write === true,
  loading: false,
  fetched: false,
  error: null,

  fetchRole: async (force = false) => {
    const state = get();

    // If already fetched and not forcing, skip
    if (state.fetched && !force) return;

    // Share an in-flight fetch, including focus and timer revalidation.
    if (fetchRolePromise) {
      return fetchRolePromise;
    }

    // Create and store the promise
    const generation = ++fetchRoleGeneration;
    fetchRolePromise = (async () => {
      set(force && state.fetched
        ? { loading: true, error: null }
        : { loading: true, error: null, permissions: null, currentRole: null, isAdmin: false });
      try {
        const orgId = window.location.pathname.match(/\/org\/([^/]+)/)?.[1];
        if (!orgId) throw new Error("Organization required");
        const response = await fetch(`/api/settings/organizations/${orgId}/my-permissions`, { credentials: "include" });
        if (!response.ok) throw new Error("Permissions unavailable", { cause: response.status });
        const { role, permissions } = await response.json() as { role: string; permissions: RolePermissions };

        if (generation !== fetchRoleGeneration) return;
        writeCachedNavAccess(orgId, { isAdmin: role === "admin", permissions });
        set({
          currentRole: role,
          isAdmin: role === "admin",
          permissions,
          loading: false,
          fetched: true,
        });
      } catch (error) {
        if (generation !== fetchRoleGeneration) return;
        // A transient background refresh failure keeps the last known permissions so open pages stay mounted;
        // 401/403 mean access is actually gone.
        const accessRevoked = error instanceof Error && (error.cause === 401 || error.cause === 403);
        const orgId = window.location.pathname.match(/\/org\/([^/]+)/)?.[1];
        if (accessRevoked && orgId) writeCachedNavAccess(orgId, null);
        set(force && state.fetched && !accessRevoked
          ? { error: "Error fetching role", loading: false }
          : { error: "Error fetching role", loading: false, fetched: true,
            permissions: null, currentRole: null, isAdmin: false });
      } finally {
        if (generation === fetchRoleGeneration) fetchRolePromise = null;
      }
    })();

    return fetchRolePromise;
  },

  reset: () => {
    fetchRoleGeneration++;
    fetchRolePromise = null;
    set({
      currentRole: null,
      isAdmin: false,
      permissions: null,
      loading: false,
      fetched: false,
      error: null,
    });
  },
}));
